import React, { useRef, useMemo, useState, useEffect } from "react";
import { useDimensions } from "common/hooks";
import styled from "styled-components";
import * as d3 from "d3";
import { createRectanglePath } from "common/utils";

const CHART_HEIGHT = 340;
const CHART_MARGIN = { top: 10, right: 50, bottom: 70, left: 50 };
const Y_AXIS_UNIT = "Mbps";
const RADIUS = 5;
const PADDING_Y_AXIS = 10;
const MAX_PLOT_WIDTH = 60;

const ChartContainer = styled.div`
  position: relative;
`;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    width: 100%;
    height: 100%;

    .boxplotMultipleAxis {
      fill: var(--chart-axis-text-color);
      color: #929295;
      font-family: var(--chart-font-family, Arial, sans-serif);
      font-size: 9px !important;

      text {
      font-size: 9px !important;
      }

      .clickable {
        cursor: pointer;
      }

      &.xAxis {
        text {
        font-size: 11px !important;
        }
      }
    }

    .boxplots {
    .rect-total:hover {
      cursor: pointer;
    }
}
     .rect-path {
      path {
        fill: var(--Box-Plot-Background);
        stroke: var(--Text-Secondary);
        stroke-width: 1px;
      }

      &.rect-max {
          path {
            fill: #6aa84f;
          }
        }
      &.rect-min {
        path {
          fill: #e69138;
        }
      }

    }
      line {
      stroke: var(--Text-Secondary);

        &.percentile10 {
          stroke: #cc0000;
        }
        &.percentile90 {
          stroke: #3d85c6;
        }
      }

      .minMax {
      stroke: var(--Text-Secondary);
      fill: transparent;

        &.max {
          stroke: #3d85c6;
        }
        &.min {
          stroke: #cc0000;
        }
      }
        }
    }
  }
`;

const Empty = styled.div`
  width: 100%;
  height: ${CHART_HEIGHT}px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

function BoxplotMultiple({
  data,
  selection,
  setSelection,
  xField = "app",
  formatXField,
  yAxisUnit = Y_AXIS_UNIT,
  minmax = true,
  margin = CHART_MARGIN,
  onContextClick,
  fields,
}) {
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);
  const { width } = dimensions;
  const height = CHART_HEIGHT;
  const widthChart = width - margin.left - margin.right;
  const heightChart = height - margin.top - margin.bottom;
  const [hoveredCell, setHoveredCell] = useState(null);
  const hasSelection = selection && setSelection;

  const fieldColorOrderMap = fields ? fields.reduce((acc, field, index) => {
    acc[field.name] = { color: field.color, order: index };
    return acc;
  }, {}) : {};

  const xKeysItems = data
    ? data.map((item) =>
        formatXField ? formatXField(item[xField]) : item[xField]
      )
    : [];

  const xKeys = fields ? xKeysItems.sort((a, b) => {
    const aOrder = fieldColorOrderMap?.[a]?.order || 0;
    const bOrder = fieldColorOrderMap?.[b]?.order || 0;
    return aOrder - bOrder;
  }) : xKeysItems;

  const filteredData = data
    ? hasSelection
      ? data.filter((item) => selection.has(item[xField]))
      : data
    : [];

  const MinMaxValues = filteredData.reduce((acc, item) => {
    if (minmax) {
      if (item.min !== "n/a" && item.min !== null && item.min !== undefined) acc.push(item.min);
      if (item.max !== "n/a" && item.max !== null && item.max !== undefined) acc.push(item.max);
    } else {
      if (item.p10 !== "n/a" && item.p10 !== null && item.p10 !== undefined) acc.push(item.p10);
      if (item.p90 !== "n/a" && item.p90 !== null && item.p90 !== undefined) acc.push(item.p90);
    }
    return acc;
  }, []);
  let minMax = d3.extent(MinMaxValues);
  if (minMax[0] === undefined) {
    minMax = [0, 1];
  }

  const yScale = useMemo(() => {
    return d3.scaleLinear().domain(minMax).range([heightChart, 0]);
  }, [minMax.join("-"), heightChart]);

  const xScale = useMemo(() => {
    return d3.scaleBand().range([0, widthChart]).domain(xKeys).padding(0.01);
  }, [JSON.stringify(xKeys), width]);

  const yLabelsTicks = yScale.ticks(5).map((tick, i) => {
    return (
      <g key={tick}>
        <text x={-10} y={yScale(tick) + 3} fontSize={9} textAnchor="end">
          {tick}
        </text>
      </g>
    );
  });

  const handleSelect = (name, selectionType) => {
    if (selectionType === "double") {
      setSelection(new Set([name]));
      return;
    }

    if (selection.size === 1 && selection.has(name)) {
      const allFieldsSet = new Set(data.map((item) => item[xField]));
      setSelection(allFieldsSet);
      return;
    }

    const newFilterSet = new Set(selection);
    if (newFilterSet.has(name)) {
      newFilterSet.delete(name);
    } else {
      newFilterSet.add(name);
    }
    setSelection(newFilterSet);
  };

  const handleContextClick = (e, name) => {
    if (onContextClick) {
      e.preventDefault();
      onContextClick(name);
    }
  };

  const xLabelsTicks = xKeys.map((name, i) => {
    const x = xScale(name);

    if (!x) {
      return null;
    }

    return (
      <g key={name}>
        <line
          x1={x + xScale.bandwidth() / 2}
          x2={x + xScale.bandwidth() / 2}
          y1={heightChart - 2 + PADDING_Y_AXIS / 2}
          y2={heightChart + 4 + PADDING_Y_AXIS / 2}
          stroke="var(--chart-axis-text-color)"
        />
        <g
          transform={`translate(${x + xScale.bandwidth() / 2},${
            heightChart + 20
          })`}
          onClick={() => handleSelect(name, "single")}
          onDoubleClick={() => handleSelect(name, "double")}
          onContextMenu={(e) => handleContextClick(e, name)}
          className="clickable"
        >
          {hasSelection ? (
            <circle
              cx={2}
              cy={-2}
              r="4"
              fill={
                selection.has(name)
                  ? fieldColorOrderMap[name]
                    ? fieldColorOrderMap[name].color
                    : "#929295"
                  : "transparent"
              }
              stroke={
                fieldColorOrderMap[name]
                  ? fieldColorOrderMap[name].color
                  : "#929295"
              }
            />
          ) : null}
          <text
            x={hasSelection ? 8 : 0}
            y={0}
            textAnchor="begin"
            transform="rotate(30)"
          >
            {name}
          </text>
        </g>
      </g>
    );
  });

  const boxPlots = filteredData.map((item, i) => {
    const { min, max, p10, p90, p25, p50, p75 } = item;
    if (
      isNaN(min) &&
      isNaN(max) &&
      isNaN(p10) &&
      isNaN(p90) &&
      isNaN(p25) &&
      isNaN(p50) &&
      isNaN(p75)
    ) {
      return null;
    }
    const xVal = item[xField];
    const x = xScale(formatXField ? formatXField(xVal) : xVal);
    const xPos = x + xScale.bandwidth() / 2;

    const yMax = minmax ? yScale(max) : null;
    const yMin = minmax ? yScale(min) : null;
    const yP10 = yScale(p10);
    const yP90 = yScale(p90);
    const yP25 = yScale(p25);
    const yP50 = yScale(p50);
    const yP75 = yScale(p75);
    const radiusMax =
      Math.abs(yP50 - yP75) > RADIUS ? RADIUS : Math.abs(yP50 - yP75);
    const radiusMin =
      Math.abs(yP25 - yP50) > RADIUS ? RADIUS : Math.abs(yP25 - yP50);

    const width = xScale.bandwidth();
    const widthPlot =
      width * 0.7 > MAX_PLOT_WIDTH ? MAX_PLOT_WIDTH : width * 0.7;

    const xPosBox = x + (width - widthPlot) / 2;

    const maxPath = createRectanglePath({
      width: widthPlot,
      height: Math.abs(yP50 - yP75),
      bottomLeftRadius: 0,
      bottomRightRadius: 0,
      topLeftRadius: radiusMax,
      topRightRadius: radiusMax,
    });
    const minPath = createRectanglePath({
      width: widthPlot,
      height: Math.abs(yP25 - yP50),
      bottomLeftRadius: radiusMin,
      bottomRightRadius: radiusMin,
      topLeftRadius: 0,
      topRightRadius: 0,
    });

    return (
      <g key={xVal}>
        {minmax ? (
          <circle cx={xPos} cy={yMax} r="3" className="minMax max" />
        ) : null}
        {minmax ? (
          <circle cx={xPos} cy={yMin} r="3" className="minMax min" />
        ) : null}
        {minmax ? (
          <line
            x1={xPos}
            x2={xPos}
            y1={yMax}
            y2={yP90}
            className="percentile90"
            stroke-dasharray="0 3 0"
          />
        ) : null}
        <line
          x1={xPos - widthPlot / 2}
          x2={xPos + widthPlot / 2}
          y1={yP90}
          y2={yP90}
          className="percentile90"
        />
        <line
          x1={xPos}
          x2={xPos}
          y1={yP90}
          y2={yP75}
          className="percentile90"
        />
        {minmax ? (
          <line
            x1={xPos}
            x2={xPos}
            y1={yP10}
            y2={yMin}
            className="percentile10"
            stroke-dasharray="0 3 0"
          />
        ) : null}
        <line
          x1={xPos - widthPlot / 2}
          x2={xPos + widthPlot / 2}
          y1={yP10}
          y2={yP10}
          className="percentile10"
        />
        <line
          x1={xPos}
          x2={xPos}
          y1={yP25}
          y2={yP10}
          className="percentile10"
        />
        <g
          className="rect-path rect-max"
          transform={`translate(${[xPosBox, yP75].join(",")})`}
        >
          <path d={maxPath} />
        </g>
        <g
          className="rect-path rect-min"
          transform={`translate(${[xPosBox, yP50].join(",")})`}
        >
          <path d={minPath} />
        </g>
        <rect
          x={xPosBox}
          y={0}
          width={widthPlot}
          height={heightChart}
          fill={"transparent"}
          onMouseEnter={(e) => {
            setHoveredCell({ ...item, xPos, yPos: e.nativeEvent.clientY });
          }}
          onMouseLeave={() => {
            setHoveredCell(null);
          }}
          className="rect-total"
        />
      </g>
    );
  });

  return (
    <ChartContainer>
      <WrapperChart ref={chartRef} style={{ height }}>
        {!data || data.length === 0 ? (
          <Empty>No Data Available</Empty>
        ) : (
          <svg>
            <g
              width={width}
              height={height}
              transform={`translate(${[
                margin.left,
                margin.top - PADDING_Y_AXIS / 2,
              ].join(",")})`}
            >
              <text
                y={-50}
                x={-heightChart / 2}
                fill={"var(--Text-Primary)"}
                textAnchor="middle"
                transform="rotate(-90)"
              >
                {yAxisUnit}
              </text>
              <rect
                x={0}
                y={0}
                rx={5}
                width={widthChart}
                height={heightChart + PADDING_Y_AXIS}
                fill={"transparent"}
                stroke={"var(--chart-inner-border-color, #000)"}
              />
            </g>
            <g
              width={width}
              height={height}
              transform={`translate(${[margin.left, margin.top].join(",")})`}
            >
              <g className="boxplotMultipleAxis xAxis">{xLabelsTicks}</g>
              <g className="boxplotMultipleAxis yAxis">{yLabelsTicks}</g>
              <g className="boxplots">{boxPlots}</g>
            </g>
          </svg>
        )}
        <Tooltip
          interactionData={hoveredCell}
          unit={yAxisUnit}
          xField={xField}
          formatXField={formatXField}
          minmax={minmax}
        />
      </WrapperChart>
    </ChartContainer>
  );
}

export default BoxplotMultiple;

const TooltipWrapper = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;
  z-index: 200;
`;

const TooltipHeader = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
  font-size: 13px;
  font-weight: 100 !important;
`;

const TooltipValueWrapper = styled.div`
  display: flex;
  width: 180px;
  justify-content: space-between;
`;

const TooltipValue = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
`;

const TooltipLabel = styled.div`
  margin: 0 0px;
`;

const TooltipValues = styled.div`
  margin-top: 10px;
`;

const TOOLTIP_VALUES = {
  "90th percentile": "p90",
  "75th percentile": "p75",
  Median: "p50",
  "25th percentile": "p25",
  "10th percentile": "p10",
};

const Tooltip = ({ interactionData, unit, xField, formatXField, minmax }) => {
  const [tooltipValues, setTooltipValues] = useState(TOOLTIP_VALUES);

  useEffect(() => {
    if (minmax) {
      setTooltipValues({ ...TOOLTIP_VALUES, Maximum: "max", Minimum: "min" });
    } else {
      setTooltipValues({ ...TOOLTIP_VALUES });
    }
  }, [minmax]);

  if (!interactionData) {
    return null;
  }

  return (
    <div
      style={{
        height: "auto",
        width: "auto",
        position: "absolute",
        top: -10,
        left: interactionData.xPos,
        pointerEvents: "none",
      }}
    >
      <TooltipWrapper
        style={{
          position: "absolute",
          width: "auto",
          left: 60,
          top: 10,
        }}
      >
        <TooltipHeader>
          {formatXField
            ? formatXField(interactionData[xField])
            : interactionData[xField]}
        </TooltipHeader>
        <TooltipValues>
          {Object.entries(tooltipValues).map(([key, value]) => {
            return (
              <TooltipValueWrapper key={`${key}-value`}>
                <TooltipLabel>{key}</TooltipLabel>
                <TooltipValue>
                  {interactionData[value] === "n/a"
                    ? "n/a"
                    : `${d3.format(".2f")(interactionData[value])} ${unit}`}
                </TooltipValue>
              </TooltipValueWrapper>
            );
          })}
        </TooltipValues>
      </TooltipWrapper>
    </div>
  );
};
